// What an offer applies to, and when, for the offers list on a Fashion till.
// The scope comes from the rule's own product, category and brand lists
// ("3 products: Oxford Shirt, Chino Beige, Polo +1 more"), not a guess from
// its type. The weekdays and hours are the offer's schedule, shown on their
// own line rather than in place of what it applies to.
import db from "../../database";

// Offers on the bill total rather than on items.
const BILL_TYPES = new Set(["TD", "TDF", "FD"]);
export const SCOPE_NAMES_SHOWN = 3;

const DAYS = [
  ["monday", "Mon"],
  ["tuesday", "Tue"],
  ["wednesday", "Wed"],
  ["thursday", "Thu"],
  ["friday", "Fri"],
  ["saturday", "Sat"],
  ["sunday", "Sun"],
];

const uniq = (list) => [...new Set(list.filter((v) => v != null && v !== ""))];
const idsOf = (rows, key) => uniq((Array.isArray(rows) ? rows : []).map((r) => r?.[key]));

// The ids a rule names, by kind.
export function offerScopeIds(rule) {
  return {
    products: uniq([...idsOf(rule?.mPricingXProducts, "mProductId"), ...idsOf(rule?.mPricingYProducts, "mProductId")]),
    categories: idsOf(rule?.mPricingPcategories, "mProductCategoryId"),
    brands: idsOf(rule?.mPricingBrands, "mBrandId"),
  };
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function part(ids, nameMap, one, many) {
  if (ids.length === 0) return "";
  const names = ids.map((id) => nameMap?.get?.(id)).filter(Boolean);
  const head = plural(ids.length, one, many);
  if (names.length === 0) return head;
  const shown = names.slice(0, SCOPE_NAMES_SHOWN);
  const more = ids.length - shown.length;
  return `${head}: ${shown.join(", ")}${more > 0 ? ` +${more} more` : ""}`;
}

// rule + { products, categories, brands } (Maps of id → name) → one line, e.g.
// "3 products: Oxford Shirt, …" or "Everything except 1 brand: Levi's".
export function describeOfferScope(rule, names = {}) {
  if (!rule) return "";
  const ids = offerScopeIds(rule);
  const parts = [];
  const add = (text, excluded) => text && parts.push({ text, excluded });
  add(part(ids.products, names.products, "product", "products"), rule.excludeProducts === "Y");
  add(part(ids.categories, names.categories, "category", "categories"), rule.excludeProductCategories === "Y");
  add(part(ids.brands, names.brands, "brand", "brands"), rule.excludeBrands === "Y");
  if (parts.length === 0) return BILL_TYPES.has(rule.type) ? "The whole bill" : "Every item";
  const inc = parts.filter((p) => !p.excluded).map((p) => p.text);
  const exc = parts.filter((p) => p.excluded).map((p) => p.text);
  const out = [];
  if (inc.length) out.push(inc.join(" · "));
  if (exc.length) out.push(`${inc.length ? "not" : "Everything except"} ${exc.join(" · ")}`);
  return out.join(", ");
}

const hhmm = (t) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t ?? "").trim());
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
};

// The days and hours an offer runs: "Every day", "Mon, Wed, Fri",
// "Sat, Sun · 10:00–14:00".
export function describeSchedule(rule) {
  if (!rule) return "";
  const timed = rule.timeSpecific === "Y";
  const on = timed ? DAYS.filter(([k]) => rule[k] === "Y").map(([, label]) => label) : [];
  const days = on.length === 0 || on.length === DAYS.length ? "Every day" : on.join(", ");
  const from = timed ? hhmm(rule.starttime) : "";
  const to = timed ? hhmm(rule.endtime) : "";
  return from && to ? `${days} · ${from}–${to}` : days;
}

// Names for every id the rules name. Reads by key with where().anyOf() and
// whole small tables with toArray(), so it works on Dexie and SQLite alike.
// A table that can't be read just leaves its names out.
export async function loadOfferScopeNames(rules) {
  const want = { products: new Set(), categories: new Set(), brands: new Set() };
  (rules || []).forEach((r) => {
    const ids = offerScopeIds(r);
    ids.products.forEach((id) => want.products.add(id));
    ids.categories.forEach((id) => want.categories.add(id));
    ids.brands.forEach((id) => want.brands.add(id));
  });
  const names = { products: new Map(), categories: new Map(), brands: new Map() };
  const safe = async (read) => {
    try {
      return (await read()) || [];
    } catch {
      return [];
    }
  };
  const [products, categories, allCategories, brands] = await Promise.all([
    want.products.size ? safe(() => db.products.where("mProductId").anyOf([...want.products]).toArray()) : [],
    want.categories.size ? safe(() => db.productCategories.toArray()) : [],
    want.categories.size ? safe(() => db.AllProductCategories.toArray()) : [],
    want.brands.size ? safe(() => db.productBrands.toArray()) : [],
  ]);
  products.forEach((p) => p?.mProductId && p.name && names.products.set(p.mProductId, p.name));
  [...allCategories, ...categories].forEach((c) => {
    const name = c?.name || c?.value;
    if (c?.mProductCategoryId && want.categories.has(c.mProductCategoryId) && name) names.categories.set(c.mProductCategoryId, name);
  });
  brands.forEach((b) => {
    const id = b?.brandId ?? b?.mBrandId;
    if (id && want.brands.has(id) && b.name) names.brands.set(id, b.name);
  });
  return names;
}
